import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import Image from "next/image";
import { useTranslations } from "next-intl";
import type { Excursion } from "@/types/dto/pdp/pdp-itinerary";
import { ColletteChip } from "@collette-travel/collette-common-controls";

export interface ExcursionCardProps {
  excursion: Excursion;
  onClick?: (excursion: Excursion) => void;
  testIdPrefix: string;
  showChip?: boolean;
  wideCard?: boolean;
}

const ExcursionCard: React.FC<ExcursionCardProps> = ({
  excursion,
  onClick,
  testIdPrefix,
  showChip = true,
  wideCard = false
}) => {
  const t = useTranslations("PDP");

  const isClickable = !!onClick;

  const handleClick = () => {
    if (isClickable) {
      onClick(excursion);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isClickable && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <Box
      data-testid={`${testIdPrefix}_excursion-card`}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      sx={{
        display: "flex",
        gap: "1.4rem",
        backgroundColor: "background.default",
        borderRadius: "2rem",
        border: "1px solid",
        borderColor: "divider",
        cursor: isClickable ? "pointer" : "default",
        transition: "all 0.2s ease-in-out",
        overflow: "hidden",
        minHeight: "15rem",
        // WHY: Only apply interactive styles when clickable
        ...(isClickable && {
          "&:hover": {
            boxShadow: 6,
            transform: "translateY(-2px)"
          },
          "&:focus-visible": {
            outline: "2px solid var(--theme-palette-primary-main)",
            outlineOffset: "2px"
          }
        })
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: wideCard ? "160px" : "173px",
          flexShrink: 0,
          overflow: "hidden"
        }}
      >
        {excursion.imageUrl ? (
          <Image
            data-testid={`${testIdPrefix}-image`}
            src={excursion.imageUrl}
            alt={excursion.title}
            fill
            sizes="173px"
            style={{ objectFit: "cover" }}
          />
        ) : (
          <Box
            aria-hidden="true"
            sx={{
              width: "100%",
              height: "100%",
              minHeight: "15rem",
              backgroundColor: "grey.95"
            }}
          />
        )}
      </Box>

      <Stack
        flex={1}
        justifyContent={wideCard ? "space-between" : "center"}
        gap="0.8rem"
        sx={{ padding: "1.2rem 1.2rem 1.2rem 0", paddingRight: { sm: "2.4rem" } }}
      >
        {showChip && (
          <ColletteChip
            color="info"
            sx={{
              fontSize: "1.4rem !important",
              fontWeight: "600",
              zIndex: 5,
              borderRadius: "1rem",
              width: "fit-content"
            }}
            testId={`${testIdPrefix}-upgrade-optional-add-on-chip`}
            label={t("Optional Add-on")}
          />
        )}

        <Typography
          variant="body1"
          data-testid={`${testIdPrefix}-excursion-title`}
          sx={{
            fontSize: "1.6rem",
            fontWeight: 700,
            lineHeight: 1.5,
            color: "text.primary",
            minHeight: wideCard ? { sm: "6rem" } : { md: "4.8rem" },
            alignContent: wideCard ? "flex-end" : undefined
          }}
        >
          {excursion.title}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: wideCard
              ? { xs: "column", sm: "row" }
              : { xs: "column", sm: "row", md: "column", lg: "row" },
            gap: "1.2rem",
            alignContent: "flex-start"
          }}
        >
          <Typography
          data-testid={`${testIdPrefix}-excursion-price`}
            variant="body2"
            sx={{
              fontSize: "1.6rem",
              fontWeight: 500,
              lineHeight: 1.5,
              letterSpacing: "0.4px"
            }}
          >
            {excursion.price} {t("per person")}
          </Typography>
          <Typography
          data-testid={`${testIdPrefix}-excursion-view-details`}
            variant="body2"
            sx={{
              fontSize: "1.6rem",
              fontWeight: 700,
              lineHeight: 1.5,
              color: "primary.main",
              textDecoration: "underline",
              letterSpacing: "0.4px"
            }}
          >
            {t("View Details")}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default ExcursionCard;
